import type {
  WebPushPublicKeyResponse,
  WebPushSendStats,
  WebPushSubscriptionInput,
} from "../WarehouseApi";
import type { WarehouseApiModuleContext } from "./WarehouseApiModuleContext";

type ExtractApiError = (payload: unknown) => string | null;
type ToSafeNonNegativeInt = (value: unknown, fallback: number) => number;

interface PushApiModuleDependencies {
  extractApiError: ExtractApiError;
  toSafeNonNegativeInt: ToSafeNonNegativeInt;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export class PushApiModule {
  constructor(
    private readonly context: Pick<WarehouseApiModuleContext, "sendJson">,
    private readonly deps: PushApiModuleDependencies,
  ) {}

  async getWebPushPublicKey(): Promise<WebPushPublicKeyResponse> {
    const payload = await this.context.sendJson<unknown>(
      "/api/push/public-key",
      "GET",
    );
    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !isRecord(payload.data)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при получении Web Push ключа",
      );
    }

    return {
      enabled: Boolean(payload.data.enabled),
      publicKey:
        typeof payload.data.publicKey === "string" && payload.data.publicKey
          ? payload.data.publicKey
          : null,
    };
  }

  async subscribeWebPush(input: WebPushSubscriptionInput): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      "/api/push/subscriptions",
      "POST",
      {
        subscription: {
          endpoint: input.endpoint,
          expirationTime:
            typeof input.expirationTime === "number"
              ? input.expirationTime
              : null,
          remindersEnabled:
            typeof input.remindersEnabled === "boolean"
              ? input.remindersEnabled
              : true,
          keys: {
            p256dh: input.keys.p256dh,
            auth: input.keys.auth,
          },
        },
      },
    );

    if (!isRecord(payload) || payload.success !== true) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(apiError || "Не удалось сохранить Web Push подписку");
    }
  }

  async unsubscribeWebPush(endpoint: string): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      "/api/push/subscriptions",
      "DELETE",
      {
        endpoint,
      },
    );

    if (!isRecord(payload) || payload.success !== true) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(apiError || "Не удалось удалить Web Push подписку");
    }
  }

  async sendWebPushTest(input?: {
    title?: string;
    body?: string;
  }): Promise<WebPushSendStats> {
    const payload = await this.context.sendJson<unknown>(
      "/api/push/test",
      "POST",
      {
        ...(typeof input?.title === "string" ? { title: input.title } : {}),
        ...(typeof input?.body === "string" ? { body: input.body } : {}),
      },
    );

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !isRecord(payload.data)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(apiError || "Не удалось отправить тестовый Web Push");
    }

    return {
      delivered: this.deps.toSafeNonNegativeInt(payload.data.delivered, 0),
      failed: this.deps.toSafeNonNegativeInt(payload.data.failed, 0),
      expired: this.deps.toSafeNonNegativeInt(payload.data.expired, 0),
    };
  }
}
